import React from 'react';
import '../styles/sessions-empty-state.css';

/**
 * SessionsEmptyState Component
 *
 * Displays contextual empty states for the Sessions page:
 * - When no student is selected (guides user to select one)
 * - When a student has no sessions yet (explains what to expect)
 *
 * Follows LRBrain design language with navy/blue/coral palette
 */
const SessionsEmptyState = ({ type = 'no-student', studentName = null }) => {
  if (type === 'no-student') {
    return (
      <div className="sessions-empty">
        <div className="sessions-empty__card sessions-empty__card--student">
          {/* Decorative background elements */}
          <div className="sessions-empty__bg-pattern" aria-hidden="true" />

          {/* Icon with gradient background */}
          <div className="sessions-empty__icon-wrap">
            <div className="sessions-empty__icon-ring" />
            <div className="sessions-empty__icon">
              <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
                <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                <circle cx="12" cy="7" r="4" />
              </svg>
            </div>
          </div>

          <h2 className="sessions-empty__title">Select a Student</h2>
          <p className="sessions-empty__desc">
            Choose a student from the dropdown above to view their tutoring session history.
          </p>

          {/* Workflow steps */}
          <div className="sessions-empty__steps">
            <div className="sessions-empty__step">
              <span className="sessions-empty__step-num">1</span>
              <div className="sessions-empty__step-content">
                <span className="sessions-empty__step-title">Select a student</span>
                <span className="sessions-empty__step-desc">Use the dropdown in the header</span>
              </div>
            </div>
            <div className="sessions-empty__step-connector" aria-hidden="true" />
            <div className="sessions-empty__step">
              <span className="sessions-empty__step-num">2</span>
              <div className="sessions-empty__step-content">
                <span className="sessions-empty__step-title">Browse session history</span>
                <span className="sessions-empty__step-desc">View past tutoring sessions</span>
              </div>
            </div>
            <div className="sessions-empty__step-connector" aria-hidden="true" />
            <div className="sessions-empty__step">
              <span className="sessions-empty__step-num">3</span>
              <div className="sessions-empty__step-content">
                <span className="sessions-empty__step-title">Review insights</span>
                <span className="sessions-empty__step-desc">Explore summaries and progress</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // No sessions for selected student
  return (
    <div className="sessions-empty">
      <div className="sessions-empty__card sessions-empty__card--sessions">
        {/* Decorative background elements */}
        <div className="sessions-empty__bg-pattern sessions-empty__bg-pattern--alt" aria-hidden="true" />

        {/* Icon with gradient background */}
        <div className="sessions-empty__icon-wrap sessions-empty__icon-wrap--alt">
          <div className="sessions-empty__icon-ring sessions-empty__icon-ring--alt" />
          <div className="sessions-empty__icon sessions-empty__icon--alt">
            <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
              <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
              <line x1="16" y1="2" x2="16" y2="6" />
              <line x1="8" y1="2" x2="8" y2="6" />
              <line x1="3" y1="10" x2="21" y2="10" />
              <circle cx="12" cy="15" r="2" />
            </svg>
          </div>
        </div>

        <h2 className="sessions-empty__title">No Sessions Yet</h2>
        <p className="sessions-empty__desc">
          {studentName
            ? `${studentName} doesn't have any recorded sessions yet.`
            : `This student doesn't have any recorded sessions yet.`
          }
        </p>

        {/* Info card */}
        <div className="sessions-empty__info">
          <div className="sessions-empty__info-icon">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="10" />
              <line x1="12" y1="16" x2="12" y2="12" />
              <line x1="12" y1="8" x2="12.01" y2="8" />
            </svg>
          </div>
          <div className="sessions-empty__info-content">
            <span className="sessions-empty__info-title">How sessions are added</span>
            <p className="sessions-empty__info-text">
              Sessions appear here after tutoring sessions are recorded, synced from Google Meet,
              or created via the whiteboard. Each session includes AI-generated summaries and insights.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SessionsEmptyState;
